import { type FC, useState, useRef, useEffect } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import { acceptInvitation } from "@/shared/utils/invitationAcceptance";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { getHelpTopicsFromAnswers } from "@/shared/constants/helpTopics";

const OTP_LENGTH = 6;

export function createInviteVerifyEmail(token: string): FC<FlowCustomComponentProps> {
  const InviteVerifyEmail: FC<FlowCustomComponentProps> = ({ onCompleteFlow, answers }) => {
    const queryClient = useQueryClient();
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [acceptError, setAcceptError] = useState<string | null>(null);

    const {
      verifyOtpAndCreateUser,
      resendVerificationCode,
      isLoading,
      error,
      clearError,
      pendingEmail,
    } = useOnboardingAuth({ analyticsFlow: "family_invite" });

    useEffect(() => {
      inputRefs.current[0]?.focus();
    }, []);

    const otpCode = otp.join("");
    const isComplete = otpCode.length === OTP_LENGTH;

    const fullName = (answers["invitee-full-name"] as string) || "";
    const phoneNumber = (answers["invitee-phone"] as string) || "";
    const relationship = (answers["invitee-relationship"] as string) || "";
    const helpTopics = getHelpTopicsFromAnswers(answers) ?? undefined;

    const handleVerify = async () => {
      if (isLoading || !isComplete) return;

      clearError();
      setAcceptError(null);

      const userRecord = await verifyOtpAndCreateUser(otpCode, {
        fullName,
        phoneNumber,
        helpTopics,
      });

      if (userRecord) {
        const email = pendingEmail ?? "";
        const accepted = await acceptInvitation(token, email, relationship);

        if (!accepted) {
          setAcceptError("Failed to accept invitation. Please try again.");
          return;
        }

        const { refetchCriticalQueries } = await import("@/shared/utils/queryRefetchHelper");
        await refetchCriticalQueries(queryClient);

        await queryClient.invalidateQueries({ queryKey: ["userContacts"] });
        await queryClient.invalidateQueries({ queryKey: ["personInvitations"] });
        await queryClient.invalidateQueries({ queryKey: ["personInvitationsAccepted"] });

        enqueueSnackbar(TOAST.ACCOUNT_JOINED, { variant: "success" });

        onCompleteFlow?.();
      }
    };

    const handleChange = (index: number, value: string) => {
      if (value && !/^\d$/.test(value)) return;

      clearError();
      setAcceptError(null);

      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === "Enter" && isComplete && !isLoading) {
        handleVerify();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
      if (pastedData.length === 0) return;

      clearError();
      setAcceptError(null);

      const newOtp = Array(OTP_LENGTH).fill("");
      pastedData.split("").forEach((char, index) => {
        if (index < OTP_LENGTH) {
          newOtp[index] = char;
        }
      });
      setOtp(newOtp);

      const nextEmptyIndex = newOtp.findIndex((val) => !val);
      const focusIndex = nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    };

    const handleResendCode = async () => {
      clearError();
      setAcceptError(null);
      setOtp(Array(OTP_LENGTH).fill(""));
      await resendVerificationCode();
      inputRefs.current[0]?.focus();
    };

    const displayError = acceptError ?? error;

    return (
      <div className="flex flex-col">
        {pendingEmail && (
          <p className="mb-6 text-center text-sm text-secondary">
            Sent to <span className="font-medium text-primary">{pendingEmail}</span>
          </p>
        )}

        <div className="flex gap-2 justify-center">
          {otp.map((digit, index) => (
            <Input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className="h-14 w-12 text-center text-xl font-medium"
              disabled={isLoading}
            />
          ))}
        </div>

        {displayError && (
          <p className="mt-4 text-center text-sm text-destructive">{displayError}</p>
        )}

        <p className="mt-6 text-center text-sm text-secondary">
          Didn't get it? Check your spam folder or{" "}
          <Button
            variant="link"
            size={null}
            type="button"
            onClick={handleResendCode}
            disabled={isLoading}
            className="inline text-sm text-brand underline"
          >
            resend code
          </Button>
        </p>

        <div className="mt-10">
          <Button
            variant="brand"
            rounded={true}
            onClick={handleVerify}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Continue <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return InviteVerifyEmail;
}
